/**
 * Mail/calendar provider identities and capability model.
 *
 * Platform identity (the DeskWorks One account) is always separate from a connected
 * mailbox. Google, Microsoft and Zoho connect through provider-hosted OAuth. Spaceship
 * Spacemail connects with the mailbox address and mailbox password through the protected
 * connection form; the credential is stored only in the server vault. Any other provider with
 * standard protocols connects as `imap` (address + password, usually an app password), either
 * through a reviewed preset (mail-presets.ts) or with servers the person enters.
 */
import { mailPreset } from './mail-presets.js';

export const PROVIDER_IDS = ['google', 'microsoft', 'zoho', 'spacemail', 'imap'] as const;
export type ProviderId = (typeof PROVIDER_IDS)[number];

export type AuthKind = 'oauth' | 'mailbox_password';

export type ServiceKind = 'mail' | 'calendar' | 'contacts';

/**
 * Independent status for each service of a connected account. A failure in an optional
 * service (contacts) must never hide a working mailbox.
 */
export type ServiceState =
  | 'connected'
  | 'checking'
  | 'pending'
  | 'not_enabled'
  | 'unavailable'
  | 'auth_failed'
  | 'error'
  | 'disconnected';

export interface ProviderInfo {
  id: ProviderId;
  /** Display label shown in Add account. Provider names are text only; no logos are implied. */
  label: string;
  shortLabel: string;
  authKind: AuthKind;
  services: ServiceKind[];
  /** Optional services that require separate consent. */
  optionalServices: ServiceKind[];
}

export const PROVIDERS: Record<ProviderId, ProviderInfo> = {
  google: {
    id: 'google',
    label: 'Google (Gmail)',
    shortLabel: 'Gmail',
    authKind: 'oauth',
    services: ['mail', 'calendar'],
    optionalServices: ['contacts'],
  },
  microsoft: {
    id: 'microsoft',
    label: 'Microsoft (Outlook)',
    shortLabel: 'Outlook',
    authKind: 'oauth',
    services: ['mail', 'calendar'],
    optionalServices: ['contacts'],
  },
  zoho: {
    id: 'zoho',
    label: 'Zoho Mail',
    shortLabel: 'Zoho',
    authKind: 'oauth',
    services: ['mail', 'calendar'],
    optionalServices: [],
  },
  spacemail: {
    id: 'spacemail',
    label: 'Spaceship (Spacemail)',
    shortLabel: 'Spacemail',
    authKind: 'mailbox_password',
    services: ['mail', 'calendar'],
    optionalServices: ['contacts'],
  },
  imap: {
    id: 'imap',
    label: 'Email (IMAP)',
    shortLabel: 'Email',
    authKind: 'mailbox_password',
    services: ['mail', 'calendar'],
    optionalServices: [],
  },
};

/**
 * Provider name for a connected account. IMAP accounts show their preset ("Yahoo Mail");
 * accounts on servers the person entered show "Email (IMAP)".
 */
export function accountProviderLabel(a: { provider: ProviderId | 'sample'; presetId?: string | null }): string {
  if (a.provider === 'sample') return 'Sample mailbox';
  if (a.provider === 'imap') return mailPreset(a.presetId)?.label ?? PROVIDERS.imap.label;
  return PROVIDERS[a.provider]?.label ?? a.provider;
}

export function accountProviderShortLabel(a: { provider: ProviderId | 'sample'; presetId?: string | null }): string {
  if (a.provider === 'sample') return 'Sample';
  if (a.provider === 'imap') return mailPreset(a.presetId)?.shortLabel ?? PROVIDERS.imap.shortLabel;
  return PROVIDERS[a.provider]?.shortLabel ?? a.provider;
}

export function isProviderId(value: unknown): value is ProviderId {
  return typeof value === 'string' && (PROVIDER_IDS as readonly string[]).includes(value);
}

/** Scheduling capabilities are tracked separately from event storage. */
export interface CalendarCapabilities {
  readEvents: boolean;
  writeEvents: boolean;
  /**
   * Attendee invitations / RSVP / cancellations. 'available' means a documented provider-hosted
   * scheduling path (still subject to live release tests); 'unverified' disables attendee actions.
   */
  scheduling: 'available' | 'unverified' | 'unsupported';
  freeBusy: 'available' | 'unverified' | 'unsupported';
}

export const SPACEMAIL_DISCLOSURE =
  'DeskWorks One securely stores your mailbox credential to keep mail and calendar updated and deliver notifications when the app is closed.';

/**
 * Readable recovery copy for connection failures. When the provider response is
 * ambiguous we describe what can be checked rather than claiming to know the cause.
 */
export const CONNECTION_PROBLEM_COPY = {
  auth_rejected: 'The mailbox address or mailbox password was not accepted. Check both and try again.',
  password_changed: 'This mailbox stopped accepting the saved password. If you changed it, reconnect with the new mailbox password.',
  access_disabled: 'The mail service refused access for this mailbox. Check that mail-app access is enabled for the mailbox, then reconnect.',
  service_unavailable: 'The mail service is not reachable right now. DeskWorks One will try again; your drafts and queued work are kept.',
  throttled: 'The mail service asked us to slow down. Sync will resume automatically.',
  quota: 'The mailbox is over its storage or sending limit. Free space or wait before sending again.',
  ambiguous: 'The mail service did not explain the problem. Check the mailbox address, mailbox password and that the mailbox is active, then try again.',
  oauth_revoked: 'Access for this account was revoked or expired. Reconnect to resume sync.',
} as const;

export type ConnectionProblem = keyof typeof CONNECTION_PROBLEM_COPY;
